import * as React from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import Navigation from '../shared/navigation'
import ActivitiesActionBar from '../shared/actionBars/ActivitiesActionBar'
import Daily from '../tabs/activities/views/Daily'
import Weekly from '../tabs/activities/views/Weekly'
import Monthly from '../tabs/activities/views/Monthly'
import Yearly from '../tabs/activities/views/Yearly'
import Time from '../tabs/activities/views/Time'
import CardData from '../tabs/activities/views/CardData'
import Details from '../tabs/activities/views/Details'
import ChartDetails from '../tabs/activities/views/ChartDetails'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import DriverActivityBarWithDate from '../shared/viewBars/DriverActivityBarWithDate'
import Compare from '../tabs/activities/views/Compare'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { WeeklyLegendModal } from '../tabs/activities/components/WeeklyLegendModal'
import { ActivityContextMenuProvider } from '../tabs/activities/context/ActivityContextMenuContext'
import { YearlyLegendModal } from '../tabs/activities/components/YearlyLegendModal'
import SideDriversTable from '../components/SideDriversTable'
import { CollapsibleSidePanel } from '@/modules/shared/components'
import { useIsDriverOnly } from '@/modules/shared/permissions'
import AttestationsModal from '../tabs/activities/modals/AttestationsModal'
import CardHistoryModal from '../tabs/activities/modals/CardHistoryModal'

const DriverActivitiesContent = ({
  selectedDriverId,
}: {
  selectedDriverId?: number
}) => {
  const { companyId, driverId } = useParams<{
    companyId: string
    driverId: string
  }>()
  const { type, hasVehicleSourceActivities, hasTachographEvents } = useActivity()
  const isDriverOnly = useIsDriverOnly(companyId)
  const location = useLocation()
  const [showViolations, setShowViolations] = React.useState(
    // (location.state as any)?.showViolations === true,
    true,
  )
  const [showAttestations, setShowAttestations] = React.useState(false)
  const [showDataHistory, setShowDataHistory] = React.useState(false)

  const { getDateForView } = useDate()
  const [showLegend, setShowLegend] = React.useState(false)
  const [showTrips, setShowTrips] = React.useState(false)
  // Show vehicle-sourced (gray) activities the card is missing — on by default.
  const [showTachographActivities, setShowTachographActivities] =
    React.useState(true)
  // Show the tachograph-events table — on by default.
  const [showTachographEvents, setShowTachographEvents] = React.useState(true)

  const defaultYear = getDateForView('yearly')
  const basePath = `/et/company/${companyId}/driverModule/activities/driver/${driverId}`
  return (
    <div className="tw-flex tw-h-full tw-overflow-hidden">
      {!isDriverOnly && (
        <CollapsibleSidePanel
          className="tw-flex tw-min-w-[250px] tw-flex-col tw-overflow-hidden tw-border-r tw-border-base-300"
          drawerClassName="tw-w-[300px] tw-max-w-[85vw]"
        >
          <SideDriversTable companyId={companyId} />
        </CollapsibleSidePanel>
      )}
      <div className="tw-flex tw-h-full tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-x-auto tw-overflow-y-hidden">
      <div className="tw-flex tw-h-full tw-min-w-[900px] tw-flex-1 tw-flex-col tw-overflow-hidden">
        <DriverActivityBarWithDate />

        <div className="tw-relative tw-mb-[1px] tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-content-primary">
          <Navigation
            companyId={companyId}
            selectedDriverId={selectedDriverId}
          />
          <ActivitiesActionBar
            basePath={basePath}
            showAttestations={() => setShowAttestations(true)}
            showTrips={showTrips}
            setShowTrips={setShowTrips}
            setShowViolations={setShowViolations}
            showViolations={showViolations}
            showTachographActivities={showTachographActivities}
            setShowTachographActivities={setShowTachographActivities}
            showTachographEvents={showTachographEvents}
            setShowTachographEvents={setShowTachographEvents}
            hasVehicleSourceActivities={hasVehicleSourceActivities}
            hasTachographEvents={hasTachographEvents}
            showDataHistory={() => setShowDataHistory(true)}
            companyId={companyId}
            driverId={driverId}
            onLegendClick={
              type === 'weekly' || type === 'yearly'
                ? () => setShowLegend(true)
                : undefined
            }
          />
          {showAttestations && (
            <AttestationsModal
              companyId={companyId}
              driverId={driverId}
              handleClose={() => setShowAttestations(false)}
            />
          )}
          {showDataHistory && (
            <CardHistoryModal
              handleClose={() => setShowDataHistory(false)}
              companyId={companyId}
              driverId={driverId}
            />
          )}
          <WeeklyLegendModal
            isOpen={type === 'weekly' && showLegend}
            onClose={() => setShowLegend(false)}
          />
          {showLegend && (
            <YearlyLegendModal handleClose={() => setShowLegend(false)} />
          )}
          <Routes>
            <Route
              index
              element={
                <Navigate to={`yearlyActivity/${defaultYear}`} replace />
              }
            />
            <Route
              path="dailyActivity/:date"
              element={
                <Daily
                  showViolations={showViolations}
                  showTrips={showTrips}
                  showTachographActivities={showTachographActivities}
                  showTachographEvents={showTachographEvents}
                />
              }
            />
            <Route
              path="weeklyActivity/:date"
              element={
                <Weekly
                  showViolations={showViolations}
                  showTrips={showTrips}
                  showTachographActivities={showTachographActivities}
                />
              }
            />
            <Route path="monthlyActivity/:month" element={<Monthly />} />
            <Route
              path="yearlyActivity/:year"
              element={
                <Yearly showViolations={showViolations} showTrips={showTrips} />
              }
            />

            <Route path="time" element={<Time />} />
            <Route path="cardData" element={<CardData />} />
            <Route path="details/:date" element={<Details />} />
            <Route path="chartDetails/:year" element={<ChartDetails />} />
            <Route path="compare" element={<Compare />} />
          </Routes>
        </div>
      </div>
      </div>
    </div>
  )
}

export const DriverActivitiesPage = () => {
  const { selectedDriverId } = useDrivers()

  return (
    <ActivityContextMenuProvider>
      <DriverActivitiesContent selectedDriverId={selectedDriverId} />
    </ActivityContextMenuProvider>
  )
}
